import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useCart } from "../context/CartContext";
import { FaCheckCircle, FaTimesCircle, FaArrowLeft, FaStar } from "react-icons/fa";
import TravelerDetailsModal from "../components/TravelerDetailsModal";
import usePageMeta from "../hooks/usePageMeta";
import { pageTitle, packageSchema, breadcrumbSchema } from "../lib/seo";
import "../styles/DetailPage.css";

function PackageDetailNew() {
  const { packageId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchPackage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageId]);

  async function fetchPackage() {
    try {
      const { data } = await supabase
        .from("packages")
        .select("*")
        .eq("id", packageId)
        .single();
      
      setPkg(data);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  }

  function formatPrice(price) {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(price);
  }

  function parseDuration(durationString) {
    if (!durationString) return { nights: 0, days: 0 };
    
    // Parse formats like "7N/8D", "7N8D", "7 Nights / 8 Days", etc.
    const nightsMatch = durationString.match(/(\d+)\s*N/i);
    const daysMatch = durationString.match(/(\d+)\s*D/i);
    
    return {
      nights: nightsMatch ? parseInt(nightsMatch[1]) : 0,
      days: daysMatch ? parseInt(daysMatch[1]) : 0,
      formatted: durationString
    };
  }

  function handleBookNow() {
    // Show modal instead of directly adding to cart
    setShowModal(true);
  }

  function handleModalSubmit(travelerDetails) {
    if (!pkg) return;
    
    const cartItem = {
      id: pkg.id,
      name: pkg.name,
      type: 'package',
      price: formatPrice(pkg.price),
      image: pkg.image,
      destination: `${pkg.city}, ${pkg.country}`,
      city: pkg.city,
      country: pkg.country,
    };
    
    addToCart(cartItem, travelerDetails);
    setShowModal(false);
    navigate("/cart");
  }

  usePageMeta({
    title: pkg ? pageTitle(`${pkg.name} — ${pkg.city}, ${pkg.country}`) : undefined,
    description: pkg ? `${pkg.duration || ""} holiday package to ${pkg.city}, ${pkg.country} from ${formatPrice(pkg.price)} per person. View itinerary, inclusions and book online.`.trim() : undefined,
    image: pkg?.image,
    jsonLd: pkg ? [
      packageSchema(pkg),
      breadcrumbSchema([
        { name: "Home", path: "/" },
        { name: "Holiday Packages", path: "/packages" },
        { name: pkg.name, path: `/packages/${pkg.id}` },
      ]),
    ] : undefined,
  });

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!pkg) {
    return (
      <div className="empty-state" style={{ minHeight: "60vh" }}>
        <div className="empty-state-icon">📦</div>
        <h3>Package not found</h3>
        <p>This package may have been removed or doesn't exist.</p>
        <Link to="/packages/" style={{ marginTop: 20, display: "inline-block" }}>
          <button className="card-cta">Browse Packages</button>
        </Link>
      </div>
    );
  }

  return (
    <div className="detail-page">
      {/* Hero */}
      <div className="detail-hero">
        <img
          src={pkg.image || "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=1200"}
          alt={pkg.name}
          className="detail-hero-image"
        />
        <div className="detail-hero-overlay">
          <div className="detail-hero-content">
            <h1 className="detail-title">{pkg.name}</h1>
            <p className="detail-subtitle">{pkg.city}, {pkg.country}</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="detail-content">
        {/* Main */}
        <div className="detail-main">
          <Link to="/packages/" className="back-link">
            <FaArrowLeft /> Back to Packages
          </Link>

          {/* Overview */}
          <div className="detail-section">
            <h2 className="section-title">Package Overview</h2>
            <div className="detail-meta-grid">
              <div className="meta-box">
                <div className="meta-box-label">Duration</div>
                <div className="meta-box-value">
                  {pkg.duration || "3N/4D"}
                </div>
              </div>
              <div className="meta-box">
                <div className="meta-box-label">Category</div>
                <div className="meta-box-value">
                  <span style={{
                    padding: '4px 12px',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '600',
                    background: pkg.category === 'international' ? '#dbeafe' : '#dcfce7',
                    color: pkg.category === 'international' ? '#1e40af' : '#166534'
                  }}>
                    {pkg.category === 'international' ? '✈️ International' : '🏠 Domestic'}
                  </span>
                </div>
              </div>
              <div className="meta-box">
                <div className="meta-box-label">Price per person</div>
                <div className="meta-box-value">{formatPrice(pkg.price)}</div>
              </div>
            </div>
          </div>

          {/* Itinerary */}
          {pkg.itinerary && pkg.itinerary.length > 0 && (
            <div className="detail-section">
              <h2 className="section-title">Day-wise Itinerary</h2>
              <div style={{ display: 'grid', gap: '16px' }}>
                {pkg.itinerary.map((day, index) => (
                  <div
                    key={index}
                    style={{
                      padding: '20px',
                      background: '#f8fafc',
                      borderRadius: '12px',
                      border: '1px solid #e2e8f0'
                    }}
                  >
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '700',
                      color: '#0ea5e9',
                      marginBottom: '8px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Day {day.day}
                    </div>
                    <h3 style={{
                      fontSize: '18px',
                      fontWeight: '700',
                      color: '#1e293b',
                      marginBottom: '8px'
                    }}>
                      {day.title}
                    </h3>
                    <p style={{
                      fontSize: '15px',
                      color: '#64748b',
                      lineHeight: '1.6',
                      margin: 0
                    }}>
                      {day.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Highlights */}
          {pkg.highlights && pkg.highlights.length > 0 && (
            <div className="detail-section">
              <h2 className="section-title">Highlights</h2>
              <ul className="detail-list">
                {pkg.highlights.map((item, index) => (
                  <li key={index}>
                    <FaStar />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Inclusions */}
          {pkg.inclusions && pkg.inclusions.length > 0 && (
            <div className="detail-section">
              <h2 className="section-title">What's Included</h2>
              <ul className="detail-list">
                {pkg.inclusions.map((item, index) => (
                  <li key={index}>
                    <FaCheckCircle />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Exclusions */}
          {pkg.exclusions && pkg.exclusions.length > 0 && (
            <div className="detail-section">
              <h2 className="section-title">What's Not Included</h2>
              <ul className="detail-list">
                {pkg.exclusions.map((item, index) => (
                  <li key={index}>
                    <FaTimesCircle style={{ color: "#ef4444" }} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="detail-sidebar">
          <div className="booking-card">
            <div className="booking-price">{formatPrice(pkg.price)}</div>
            <div className="booking-label">per person</div>
            <button className="booking-cta" onClick={handleBookNow}>
              Book Now
            </button>
            <div style={{ marginTop: 20, paddingTop: 20, borderTop: "1px solid var(--border)" }}>
              <div style={{ fontSize: 14, color: "var(--text-light)", marginBottom: 12 }}>
                <strong>Duration:</strong> {parseDuration(pkg.duration).nights} Nights, {parseDuration(pkg.duration).days} Days
              </div>
              <div style={{ fontSize: 14, color: "var(--text-light)" }}>
                <strong>Location:</strong> {pkg.city}, {pkg.country}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Traveler Details Modal */}
      <TravelerDetailsModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={handleModalSubmit}
        itemName={pkg?.name}
      />
    </div>
  );
}

export default PackageDetailNew;
